import { Box, TextField } from "@material-ui/core"
import type { SelectProps } from "@material-ui/core"
import CheckCircleIcon from "@material-ui/icons/CheckCircle"
import type { ChangeEvent, ReactElement, ReactNode } from "react"
import type { Control, Path, PathValue, Validate, ValidationRule } from "react-hook-form"
import { Controller } from "react-hook-form"

import { Body1 } from ".."

import "./text-input-field.styles.scss"
import { useTranslation } from "react-i18next"

export interface ITextInputFieldProps<T> {
  className?: string
  name: Path<T>
  control?: Control<T>
  label?: string
  placeholder?: string
  type?: string
  fullWidth?: boolean | undefined
  multiline?: boolean | undefined
  required?: boolean | string | undefined
  maxLength?: number | undefined
  minLength?: ValidationRule<string | number> | undefined
  disabled?: boolean | undefined
  valid?: boolean | undefined
  validate?: Validate<PathValue<T, Path<T>>> | Record<string, Validate<PathValue<T, Path<T>>>> | undefined
  rows?: number
  subLabel?: string
  select?: boolean | undefined
  selectProps?: Partial<SelectProps> | undefined
  onSelect?: (
    changeHandler: (event: ChangeEvent<HTMLInputElement>) => void
  ) => (event: ChangeEvent<HTMLInputElement>) => void
  children?: ReactNode
  defaultValue?: string | number | boolean
  onClick?: () => void
}

export function TextInputField<T>(props: ITextInputFieldProps<T>): ReactElement {
  const {
    className,
    name,
    label,
    placeholder,
    multiline,
    rows,
    type,
    fullWidth,
    control,
    required,
    maxLength,
    minLength,
    disabled,
    valid,
    validate,
    subLabel,
    select,
    selectProps,
    onSelect,
    children,
    defaultValue,
    onClick
  } = props
  const { t } = useTranslation("general")

  return (
    <Box className={`text-input-field ${fullWidth ? "full-width" : ""} ${className || ""}`}>
      <Controller
        name={name}
        control={control}
        rules={{
          required,
          maxLength,
          minLength,
          validate
        }}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <>
            {label && (
              <Box className="text-input-field-labels" display="flex" justifyContent="space-between">
                <Body1>
                  <b>{label}</b>
                </Body1>
                {(required || subLabel) && (
                  <Body1 className="sub-label">
                    {required ? `(${t("required").toLowerCase()})` : ""}
                    {subLabel ? subLabel : ""}
                  </Body1>
                )}
              </Box>
            )}
            <TextField
              className="text-input-field-control"
              fullWidth={fullWidth}
              type={type}
              placeholder={placeholder}
              multiline={multiline}
              onClick={onClick}
              variant="outlined"
              rows={rows}
              rowsMax={rows}
              autoComplete="off"
              disabled={disabled}
              select={select}
              SelectProps={selectProps}
              onChange={onSelect ? onSelect(onChange) : onChange}
              value={value as string}
              onBlur={onBlur}
              ref={ref}
              inputProps={{ maxLength }}
              InputProps={
                required && valid
                  ? {
                      endAdornment: <CheckCircleIcon style={{ color: "#137B13" }} />
                    }
                  : undefined
              }
              defaultValue={select ? (defaultValue ? defaultValue : "") : undefined}
            >
              {children ? children : <></>}
            </TextField>
          </>
        )}
      />
    </Box>
  )
}
